import {
    IdcardOutlined,
    DownloadOutlined,
    SearchOutlined,
    ClearOutlined,
    ColumnHeightOutlined,
    CloseCircleOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    DatePicker,
    Row,
    Col,
    Input,
    Table,
    Select,
    message,
    Modal,
    Checkbox,
    Dropdown,
    Tooltip,
    Tag,
} from "antd";
import { useState, useContext, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

const ALL_COLUMNS = [
    { key: "EmployeeCode", title: "Employee Code", dataIndex: "EmployeeCode", defaultVisible: true, fixed: "left", width: 120 },
    { key: "EmployeeName", title: "Employee Name", dataIndex: "EmployeeName", defaultVisible: true, fixed: "left", width: 180 },
    { key: "Date", title: "Attendance Date", dataIndex: "Date", defaultVisible: true, width: 130 },
    { key: "PunchIn", title: "Punch-In", dataIndex: "PunchIn", defaultVisible: true, width: 100 },
    { key: "CheckIn", title: "Check-In", dataIndex: "CheckIn", defaultVisible: true, width: 100 },
    { key: "PunchOut", title: "Punch-Out", dataIndex: "PunchOut", defaultVisible: true, width: 100 },
    { key: "CheckOut", title: "Check-Out", dataIndex: "CheckOut", defaultVisible: true, width: 100 },
    { key: "OriginalStatus", title: "Original Status", dataIndex: "status", defaultVisible: true },
    { key: "AttendanceStatus", title: "Status", dataIndex: "AttendanceStatus", defaultVisible: true },
    { key: "status_message", title: "Remarks", dataIndex: "status_message", defaultVisible: true, width: 200 },
    { key: "TotalTime", title: "Total Time", dataIndex: "TotalTime", defaultVisible: true },
    { key: "WorkingHours", title: "Working Hours", dataIndex: "OfficeWorkingHours", defaultVisible: true },
    { key: "IsSpecial", title: "Is Special", dataIndex: "is_special", defaultVisible: true },
    { key: "IsShift", title: "Is Shift", dataIndex: "is_shift", defaultVisible: true },

    { key: "Organisation", title: "Company Name", dataIndex: "Organisation", defaultVisible: false, width: 200 },
    { key: "Department", title: "Department", dataIndex: "Department", defaultVisible: false, width: 150 },
    { key: "Designation", title: "Designation", dataIndex: "Designation", defaultVisible: false, width: 150 },
    { key: "WorkLocation", title: "Work Location", dataIndex: "WorkLocation", defaultVisible: false, width: 150 },

    { key: "check_in_photo_url", title: "Check-In Photo", dataIndex: "check_in_photo_url", defaultVisible: false, width: 120 },
    { key: "CheckInLocation", title: "Check-In Location", dataIndex: "CheckInLocation", defaultVisible: false },
    { key: "CheckInWorkSite", title: "Check In Site", dataIndex: "CheckInWorkSite", defaultVisible: false },

    { key: "check_out_photo_url", title: "Check-Out Photo", dataIndex: "check_out_photo_url", defaultVisible: false, width: 120 },
    { key: "CheckOutLocation", title: "Check-Out Location", dataIndex: "CheckOutLocation", defaultVisible: false },
    { key: "CheckOutWorkSite", title: "Check Out Site", dataIndex: "CheckOutWorkSite", defaultVisible: false },

    { key: "FirstInType", title: "First In Type", dataIndex: "FirstInType", defaultVisible: false, width: 120 },
    { key: "LastOutType", title: "Last Out Type", dataIndex: "LastOutType", defaultVisible: false, width: 120 },
];

// ── Helper: normalise boolean / string values for display & export ────────────
const formatBooleanValue = (value) => {
    if (value === true || value === "true" || value === 1 || value === "1" || value === "yes" || value === "Yes") return "Yes";
    if (value === false || value === "false" || value === 0 || value === "0" || value === "no" || value === "No") return "No";
    if (value === null || value === undefined || value === "") return "-";
    return String(value);
};

export default function AttendanceSite() {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(100);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const isMobile = window.innerWidth < 768;

    const { authData, backUrl } = useContext(AuthContext);

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [isPayroll, setIsPayroll] = useState(false);

    const emptyFilters = { emp_code: "", emp_name: "", organisation: "", department: "", designation: "", from_date: null, to_date: null, status: "" };
    const [filterInputs, setFilterInputs] = useState(emptyFilters);
    const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // ── Column visibility ─────────────────────────────────────────────────────
    const [visibleColumns, setVisibleColumns] = useState(() => {
        const saved = localStorage.getItem("attendanceSite_visibleColumns");
        if (saved) { try { return JSON.parse(saved); } catch (_) { } }
        return ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);
    });
    const [columnDropdownOpen, setColumnDropdownOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem("attendanceSite_visibleColumns", JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    const handleColumnToggle = (key, e) => {
        e.stopPropagation();
        setVisibleColumns((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };
    const handleSelectAll = (e) => { e.stopPropagation(); setVisibleColumns(ALL_COLUMNS.map((c) => c.key)); };
    const handleDeselectAll = (e) => { e.stopPropagation(); setVisibleColumns([]); };
    const handleResetDefault = (e) => { e.stopPropagation(); setVisibleColumns(ALL_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)); };

    const columnSelectorMenu = {
        items: [
            {
                key: "actions",
                label: (
                    <div style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0", marginBottom: 8 }}>
                        <Button size="small" type="link" onClick={handleSelectAll} style={{ color: "#0e497b" }}>Select All</Button>
                        <Button size="small" type="link" onClick={handleDeselectAll} style={{ color: "#0e497b" }}>Deselect All</Button>
                        <Button size="small" type="link" onClick={handleResetDefault} style={{ color: "#0e497b" }}>Reset</Button>
                        <Button
                            size="small" icon={<CloseCircleOutlined />} type="primary"
                            onClick={() => setColumnDropdownOpen(false)}
                            style={{ float: "right", backgroundColor: "white", color: "#0e497b" }}
                        />
                    </div>
                ),
            },
            ...ALL_COLUMNS.map((col) => ({
                key: col.key,
                label: (
                    <Checkbox
                        checked={visibleColumns.includes(col.key)}
                        onChange={(e) => handleColumnToggle(col.key, e)}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {col.title}
                    </Checkbox>
                ),
            })),
        ],
    };

    useEffect(() => {
        if (authData?.employee?.user?.username === "BCPL569") setIsPayroll(true);
    }, [authData]);

    // ── Data fetching ─────────────────────────────────────────────────────────
    const fetchData = async (filters, page, size) => {
        setLoading(true);
        try {
            const params = {};
            if (filters.emp_code?.trim()) params.emp_code = filters.emp_code.trim();
            if (filters.emp_name?.trim()) params.emp_name = filters.emp_name.trim();
            if (filters.organisation) params.organisation = filters.organisation;
            if (filters.department) params.department = filters.department;
            if (filters.designation) params.designation = filters.designation;
            if (filters.from_date) params.from_date = dayjs(filters.from_date).format("YYYY-MM-DD");
            if (filters.to_date) params.to_date = dayjs(filters.to_date).format("YYYY-MM-DD");
            if (filters.status) params.status = filters.status;

            const res = await axios.get(`${backUrl}/api/hr/attendance-siteoffice/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
                params,
            });

            let data = [], count = 0;
            if (Array.isArray(res.data)) { data = res.data; count = res.data.length; }
            else if (res.data.results && Array.isArray(res.data.results)) { data = res.data.results; count = res.data.count || data.length; }
            else if (res.data.data && Array.isArray(res.data.data)) { data = res.data.data; count = res.data.count || data.length; }

            setDataSource(data); setTotalItems(count);
            if (data.length === 0 && Object.keys(params).length > 0) message.info("No records found matching the filters");
        } catch (error) {
            message.error(error.response?.data?.message || "Failed to fetch site attendance data");
            setDataSource([]); setTotalItems(0);
        } finally { setLoading(false); }
    };

    const fetchOrganizations = async () => {
        try { const r = await axios.get(`${backUrl}/api/emp/organisations/`); setOrganisations(r.data.results || r.data); } catch (_) { }
    };
    const fetchDepartments = async (id) => {
        try { const r = await axios.get(`${backUrl}/api/emp/departments/?organisation=${id}`); setDepartments(r.data.results || r.data); } catch (_) { }
    };
    const fetchDesignations = async (id) => {
        try { const r = await axios.get(`${backUrl}/api/emp/designations/?department=${id}`); setDesignations(r.data.results || r.data); } catch (_) { }
    };

    useEffect(() => { fetchOrganizations(); fetchData(appliedFilters, currentPage, pageSize); }, []);

    const handleApplyFilters = () => {
        setAppliedFilters({ ...filterInputs }); setCurrentPage(1); setSelectedRowKeys([]);
        fetchData(filterInputs, 1, pageSize);
    };
    const handleClearFilters = () => {
        setFilterInputs(emptyFilters); setAppliedFilters(emptyFilters);
        setDepartments([]); setDesignations([]);
        setCurrentPage(1); setSelectedRowKeys([]);
        fetchData(emptyFilters, 1, pageSize);
    };
    const handleTableChange = (page, size) => {
        if (size !== pageSize) { setPageSize(size); setCurrentPage(1); fetchData(appliedFilters, 1, size); }
        else { setCurrentPage(page); fetchData(appliedFilters, page, size); }
    };

    const handlePermissionClick = (record) => { setSelectedRecord(record); setRemarks(""); setIsModalVisible(true); };
    const handleSubmitPermission = async () => {
        if (!selectedRecord) return;
        if (!remarks.trim()) { message.warning("Please enter remarks before submitting"); return; }
        setSubmitting(true);
        try {
            await axios.post(
                `${backUrl}/api/hr/attendance-requests/`,
                { attendance_id: selectedRecord.id, employee: selectedRecord.Emp_id, remarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success("Permission consideration submitted successfully!");
            setIsModalVisible(false); setRemarks(""); setSelectedRecord(null);
            fetchData(appliedFilters, currentPage, pageSize);
        } catch (error) {
            message.error(error.response?.data?.data || error.response?.data?.message || "Failed to submit permission consideration");
        } finally { setSubmitting(false); }
    };
    const handleModalCancel = () => { setIsModalVisible(false); setRemarks(""); setSelectedRecord(null); };

    // ── Excel Download (FIXED) ────────────────────────────────────────────────
    const handleDownloadExcel = () => {
        const exportData =
            selectedRowKeys.length > 0
                ? dataSource.filter((item) => selectedRowKeys.includes(item.id || item.EmployeeCode))
                : dataSource;

        if (!exportData.length) { message.warning("No data to export!"); return; }

        const activeCols = ALL_COLUMNS.filter((c) => visibleColumns.includes(c.key));

        const excelRows = exportData.map((row) => {
            const r = {};
            activeCols.forEach((col) => {
                // Photos → text indicator
                if (col.key === "check_in_photo_url" || col.key === "check_out_photo_url") {
                    r[col.title] = row[col.dataIndex] ? "Photo Available" : "-";
                    return;
                }
                // Status normalisation (mirrors table render)
                if (col.key === "AttendanceStatus") {
                    r[col.title] = row[col.dataIndex] === "Sunday" ? "Week Off" : (row[col.dataIndex] ?? "");
                    return;
                }
                // Boolean fields
                if (col.key === "IsSpecial" || col.key === "IsShift") {
                    r[col.title] = formatBooleanValue(row[col.dataIndex]);
                    return;
                }
                // WorkingHours uses a different dataIndex (OfficeWorkingHours) — handled by col.dataIndex correctly
                r[col.title] = row[col.dataIndex] ?? "";
            });
            return r;
        });

        const ws = XLSX.utils.json_to_sheet(excelRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Site Office Attendance");
        XLSX.writeFile(wb, "SiteOffice_Attendance.xlsx");
        message.success("Excel Downloaded!");
    };

    // ── Build table columns ───────────────────────────────────────────────────
    const getTableColumns = () => {
        const defs = {};
        ALL_COLUMNS.forEach((cfg) => {
            const base = { title: cfg.title, dataIndex: cfg.dataIndex, key: cfg.key };
            if (cfg.fixed) base.fixed = cfg.fixed;
            if (cfg.width) base.width = cfg.width;

            switch (cfg.key) {
                case "check_in_photo_url":
                case "check_out_photo_url":
                    defs[cfg.key] = {
                        ...base,
                        render: (url) =>
                            url ? (
                                <img
                                    src={url} alt={cfg.title}
                                    style={{ width: 50, height: 50, objectFit: "cover", borderRadius: 4, cursor: "pointer" }}
                                    onClick={() => window.open(url, "_blank")}
                                />
                            ) : "-",
                    };
                    break;

                case "CheckInLocation":
                    defs[cfg.key] = {
                        ...base,
                        render: (text, record) =>
                            record.check_in_latitude && record.check_in_longitude ? (
                                <a
                                    href={`https://www.google.com/maps?q=${record.check_in_latitude},${record.check_in_longitude}`}
                                    target="_blank" rel="noopener noreferrer" style={{ color: "#1677ff" }}
                                >
                                    {text || "View Location"}
                                </a>
                            ) : (text || "-"),
                    };
                    break;

                case "CheckOutLocation":
                    defs[cfg.key] = {
                        ...base,
                        render: (text, record) =>
                            record.check_out_latitude && record.check_out_longitude ? (
                                <a
                                    href={`https://www.google.com/maps?q=${record.check_out_latitude},${record.check_out_longitude}`}
                                    target="_blank" rel="noopener noreferrer" style={{ color: "#1677ff" }}
                                >
                                    {text || "View Location"}
                                </a>
                            ) : (text || "-"),
                    };
                    break;

                case "CheckInWorkSite":
                    defs[cfg.key] = {
                        ...base,
                        render: (v, record) => (
                            <>
                                <p>{v || "-"}</p>
                                {record.onduty_type_check_in && <> ({record.onduty_type_check_in})</>}
                            </>
                        ),
                    };
                    break;

                case "CheckOutWorkSite":
                    defs[cfg.key] = {
                        ...base,
                        render: (v, record) => (
                            <>
                                <p>{v || "-"}</p>
                                {record.onduty_type_check_out && <> ({record.onduty_type_check_out})</>}
                            </>
                        ),
                    };
                    break;

                case "AttendanceStatus":
                    defs[cfg.key] = {
                        ...base,
                        render: (v) => (v === "Sunday" ? "Week Off" : v),
                    };
                    break;

                // ── FIXED: is_special & is_shift ──────────────────────────────
                case "IsSpecial":
                    defs[cfg.key] = {
                        ...base,
                        render: (v) => {
                            const label = formatBooleanValue(v);
                            if (label === "Yes") return <Tag color="green">Yes</Tag>;
                            if (label === "No") return <Tag color="default">No</Tag>;
                            return <span>-</span>;
                        },
                    };
                    break;

                case "IsShift":
                    defs[cfg.key] = {
                        ...base,
                        render: (v) => {
                            const label = formatBooleanValue(v);
                            if (label === "Yes") return <Tag color="blue">Yes</Tag>;
                            if (label === "No") return <Tag color="default">No</Tag>;
                            return <span>-</span>;
                        },
                    };
                    break;

                default:
                    defs[cfg.key] = base;
            }
        });

        return visibleColumns.filter((k) => defs[k]).map((k) => defs[k]);
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <section>
            <center>
                {isMobile ? (
                    <p style={{ fontSize: "15px", textTransform: "uppercase", color: "#0e497b" }}>
                        <IdcardOutlined /> Site Office Attendance Records
                    </p>
                ) : (
                    <h5><IdcardOutlined /> Site Office Attendance Records</h5>
                )}
            </center>

            {/* Filters Card */}
            <Card style={{ margin: "20px", padding: "20px", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Employee Code</label>
                        <Input
                            placeholder="Enter Employee Code"
                            value={filterInputs.emp_code}
                            onChange={(e) => setFilterInputs({ ...filterInputs, emp_code: e.target.value })}
                            onPressEnter={handleApplyFilters}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Employee Name</label>
                        <Input
                            placeholder="Enter Employee Name"
                            value={filterInputs.emp_name}
                            onChange={(e) => setFilterInputs({ ...filterInputs, emp_name: e.target.value })}
                            onPressEnter={handleApplyFilters}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Company Name</label>
                        <Select
                            placeholder="Select Company" allowClear style={{ width: "100%" }}
                            value={filterInputs.organisation || undefined}
                            onChange={(value) => {
                                setFilterInputs({ ...filterInputs, organisation: value, department: "", designation: "" });
                                if (value) fetchDepartments(value); else { setDepartments([]); setDesignations([]); }
                            }}
                        >
                            {organisations.map((org) => <Option key={org.id} value={org.id}>{org.name}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Department</label>
                        <Select
                            showSearch placeholder="Select Department" allowClear style={{ width: "100%" }}
                            value={filterInputs.department || undefined}
                            onChange={(value) => {
                                setFilterInputs({ ...filterInputs, department: value, designation: "" });
                                if (value) fetchDesignations(value); else setDesignations([]);
                            }}
                            filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}
                        >
                            {departments.map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                        </Select>
                    </Col>
                </Row>

                <Row gutter={[16, 16]} style={{ marginTop: "10px" }}>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Designation</label>
                        <Select
                            showSearch placeholder="Select Designation" allowClear style={{ width: "100%" }}
                            value={filterInputs.designation || undefined}
                            onChange={(value) => setFilterInputs({ ...filterInputs, designation: value })}
                            filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}
                        >
                            {designations.map((d) => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={6}>
                        <label>Attendance Status</label>
                        <Select
                            placeholder="Select Status" allowClear style={{ width: "100%" }}
                            value={filterInputs.status || undefined}
                            onChange={(value) => setFilterInputs({ ...filterInputs, status: value })}
                        >
                            <Option value="PRESENT">Present</Option>
                            <Option value="PERMISSION">Permission</Option>
                            <Option value="ABSENT">Absent</Option>
                            <Option value="HOLIDAY">Holiday</Option>
                            <Option value="Weekoff">Week Off</Option>
                            <Option value="Compoff">Comp Off</Option>
                            <Option value="CASUAL LEAVE">Casual Leave</Option>
                            <Option value="SICK LEAVE">Sick Leave</Option>
                            <Option value="HALFDAY_LEAVE">Half Day</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={10} lg={6}>
                        <label>Date Range</label>
                        <RangePicker
                            style={{ width: "100%" }} format="DD/MM/YYYY"
                            value={
                                filterInputs.from_date && filterInputs.to_date
                                    ? [dayjs(filterInputs.from_date), dayjs(filterInputs.to_date)]
                                    : null
                            }
                            onChange={(dates) =>
                                setFilterInputs({
                                    ...filterInputs,
                                    from_date: dates ? dates[0] : null,
                                    to_date: dates ? dates[1] : null,
                                })
                            }
                        />
                    </Col>
                    <Col xs={24} sm={24} md={24} lg={6} style={{ display: "flex", alignItems: "flex-end", gap: "10px" }}>
                        <Button
                            type="primary" icon={<SearchOutlined />}
                            onClick={handleApplyFilters} loading={loading}
                            style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", height: "36px", flex: 1 }}
                        >
                            Apply Filters
                        </Button>
                        <Button icon={<ClearOutlined />} onClick={handleClearFilters} style={{ height: "36px", flex: 1 }}>
                            Clear Filters
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Action Bar */}
            <div style={{ margin: "0 20px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
                <Button
                    icon={<DownloadOutlined />}
                    style={{ height: "36px", width: "220px", backgroundColor: "#0e497b", color: "white" }}
                    onClick={handleDownloadExcel}
                    disabled={dataSource.length === 0}
                >
                    Download {selectedRowKeys.length > 0 ? `Selected (${selectedRowKeys.length})` : "All"}
                </Button>

                <Dropdown
                    menu={columnSelectorMenu}
                    trigger={["click"]}
                    placement="bottomRight"
                    open={columnDropdownOpen}
                    onOpenChange={setColumnDropdownOpen}
                >
                    <Tooltip title="Customize Columns">
                        <Button icon={<ColumnHeightOutlined />}>
                            Custom Columns ({visibleColumns.length}/{ALL_COLUMNS.length})
                        </Button>
                    </Tooltip>
                </Dropdown>
            </div>

            {/* Table */}
            <Table
                style={{ margin: "0 20px 20px" }}
                columns={getTableColumns()}
                dataSource={dataSource}
                bordered
                loading={loading}
                pagination={{
                    current: currentPage, pageSize, total: totalItems,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50", "100"],
                    onChange: handleTableChange,
                    showTotal: (total, range) =>
                        total > 0 ? `${range[0]}-${range[1]} of ${total} records` : "0 records",
                }}
                scroll={{ x: "max-content", y: 600 }}
                rowSelection={{
                    selectedRowKeys,
                    onChange: (keys) => setSelectedRowKeys(keys),
                    preserveSelectedRowKeys: true,
                }}
                rowKey={(record) => record.id || record.EmployeeCode}
                components={{
                    header: {
                        cell: (props) => (
                            <th {...props} style={{ backgroundColor: "#0e497b", color: "white", fontWeight: "600" }}>
                                {props.children}
                            </th>
                        ),
                    },
                }}
            />

            {/* Permission Modal */}
            <Modal
                title="Permission Consideration"
                open={isModalVisible}
                onOk={handleSubmitPermission}
                onCancel={handleModalCancel}
                confirmLoading={submitting}
                okText="Submit" cancelText="Cancel" width={600}
            >
                {selectedRecord && (
                    <div style={{ marginBottom: "20px" }}>
                        <p><strong>Employee:</strong> {selectedRecord.EmployeeName} ({selectedRecord.EmployeeCode})</p>
                        <p><strong>Date:</strong> {selectedRecord.Date}</p>
                        <p><strong>Department:</strong> {selectedRecord.Department}</p>
                    </div>
                )}
                <div>
                    <label style={{ display: "block", marginBottom: "8px", fontWeight: "500" }}>Remarks:</label>
                    <TextArea
                        rows={4} placeholder="Enter your remarks here..."
                        value={remarks} onChange={(e) => setRemarks(e.target.value)}
                        maxLength={500} showCount
                    />
                </div>
            </Modal>
        </section>
    );
}